import React, { MouseEvent, useEffect, useState } from 'react'
import { Button, Card, Col, Divider, Modal, Rate, Row, Select, Skeleton, Space, Tooltip, Typography } from 'antd'
import { RouteComponentProps, useHistory } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import ISO6391 from 'iso-639-1'

import ProfileAvatar from '../../helpers/ProfileAvatar/ProfileAvatar'
import BookingModal from '../../helpers/BookingModal/BookingModal'
import ReviewModal from '../../helpers/ReviewModal/ReviewModal'
import Review from '../../helpers/Review/Review'

import { useExpertsContext } from '../../../context/experts'
import { useSearchContext } from '../../../context/search'
import { useChatContext } from '../../../context/chat'
import { useAuthContext } from '../../../context/auth'
import { sortCertifications } from '../../../utils/sort'

import { ICertification, TExpertPage, TExpertsFilter, TKeyword } from '../../../context/types'
import { RoutePath } from '../../routes'
import PageNotFound from '../../helpers/PageNotFound'
import AdminReportModal from './AdminReportModal'
import { useBrandContext } from '../../../context/brand'
import QueryString from 'query-string'

import './expert.less'
import CallPrice from '../../helpers/CallPrice/CallPrice'
import { currentBrand } from '../../../config'
import FavoritesButton from '../../helpers/FavoritesButton/FavoritesButton'
import { toggleFavoriteExpert } from '../../../context/experts/utils'
import { useChannelContext } from '../../../context/channel'

const { Title, Paragraph } = Typography

type TParams = {
	slug: string
}

function ExpertSlugPage({
	match: {
		params: { slug },
	},
}: RouteComponentProps<TParams>) {
	const { experts, actions } = useExpertsContext()
	const { actions: searchActions } = useSearchContext()
	const { chat, actions: chatActions } = useChatContext()
	const { brand } = useBrandContext()
	const {
		channel: { selectedChannel },
	} = useChannelContext()
	const { auth } = useAuthContext()
	const history = useHistory()
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [isReviewModalVisible, setIsReviewModalVisible] = useState(false)
	const [isAdminReportModal, setAdminReportModal] = useState(false)
	const [isFavorite, setFavorite] = useState<boolean>(false)
	const [selectedExpertPage, setSelectedPage] = useState<TExpertPage>(null)

	const handleFavoritesClick = (e: MouseEvent<HTMLAnchorElement>) => {
		e.stopPropagation()

		toggleFavoriteExpert(experts.expert.id).then(setFavorite)
	}

	useEffect(() => {
		actions.getExpertBySlug(slug)
		return () => {
			actions.resetExpert()
		}
	}, [slug])

	useEffect(() => {
		const query: { [key: string]: any } = QueryString.parse(history.location.search, {
			arrayFormat: 'comma',
			parseNumbers: true,
		})

		if (experts.expert && query.showBookingModal) {
			const historySearch = QueryString.stringify(
				{
					...query,
					showBookingModal: undefined,
				},
				{ arrayFormat: 'comma' }
			)
			history.replace({ search: historySearch })
			setIsModalVisible(true)
		}

		if (experts?.expert?.profile?.pages?.length) {
			setSelectedPage(experts?.expert?.profile?.pages.find((v) => v.id === experts.expert.id))
		}
	}, [experts.expert])

	const handleSearchKeyword = (keyword: TKeyword) => () => {
		searchActions.setValue(keyword.keyword)
		const newFilter: TExpertsFilter = { keywords: [keyword.id] }
		history.push(RoutePath.Experts)
		actions.setFilter(newFilter)
	}

	const handleChat = (userId: number) => () => {
		chatActions.getOrCreateChat(userId)
	}

	const handleRequestCall = () => {
		setIsModalVisible(true)
	}

	const handleCancel = () => {
		setIsModalVisible(false)
	}

	const getLanguageName = (code: string) => {
		return ISO6391.getName(code)
	}

	const handleHowItWorks = () => {
		Modal.info({
			content: <div dangerouslySetInnerHTML={{ __html: brand.currentBrand?.how_it_works_intro }} />,
			okButtonProps: {
				shape: 'round',
			},
			maskClosable: true,
			centered: true,
			icon: false,
		})
	}

	const manageAccount = () => {
		history.push(`/account/${experts?.expert?.profile?.user.id}`)
	}

	const changeExpertPage = (expertPageId: number) => {
		const expertPage = experts.expert.profile.pages.find((v) => v.id === expertPageId)
		if (expertPage) {
			actions.getExpertBySlug(expertPage.slug)
		}
	}

	const isSameUser = experts?.expert?.profile?.user?.id !== auth?.user?.id

	const isExpertProfileNotFilled =
		(!selectedExpertPage?.bio && !experts.expert?.bio) ||
		!Boolean(experts?.expert?.profile?.user?.profile_pic_url) ||
		!experts?.expert?.profile?.user?.expertInfo?.cronofy_filled ||
		(!experts?.expert?.profile?.payment_type &&
			!selectedExpertPage?.channel?.integration_config?.expert_rules?.no_payouts)

	if (!experts?.loading && experts?.error) return <PageNotFound message={experts?.error} />

	const pageTitle = `${experts?.expert?.profile?.user?.first_name} ${experts?.expert?.profile?.user?.last_name} ${
		!brand.loading && `| ${brand.currentBrand?.name}`
	}`

	return (
		<>
			<Helmet>
				<meta charSet="utf-8" />

				{!experts.loading && <title>{pageTitle}</title>}

				<meta name="title" property="og:title" content={pageTitle} />
				<meta name="description" property="og:description" content={experts?.expert?.body} />
				<meta name="type" property="og:type" content="Article" />
				<meta name="image" property="og:image" content={experts?.expert?.profile?.user?.profile_pic_url} />
			</Helmet>

			<Card style={{ margin: 10 }}>
				<Skeleton loading={!!experts.loading} active avatar title paragraph>
					<Row gutter={[20, 20]}>
						<Col xs={24} lg={6}>
							<Row gutter={[8, 8]}>
								<Col xs={0} sm={0} md={0} lg={24} xl={24}>
									<ProfileAvatar
										size={{ xs: 0, sm: 0, md: 0, lg: 200, xl: 230, xxl: 230 }}
										src={experts?.expert?.profile?.user?.profile_pic_url}
										disableThumbnail={true}
									/>
								</Col>
								<Col xs={0} sm={0} md={0} lg={24} xl={24}>
									<Space wrap>
										{experts?.expert?.keywords.map((keyword: TKeyword) => (
											<Button
												key={keyword.id + keyword.keyword}
												type="link"
												onClick={handleSearchKeyword(keyword)}
												style={{ padding: 0, height: 20 }}
											>
												#{keyword.keyword}
											</Button>
										))}
									</Space>
								</Col>
								<Col xs={0} sm={0} md={0} lg={24} xl={24}>
									<Card style={{ boxShadow: 'none' }}>
										<Title level={3}>Rating</Title>
										<Space direction="vertical" size="large">
											<Rate
												className="expert-rate"
												value={experts?.expert?.profile?.user?.expertInfo?.expert_rating}
												disabled
												allowHalf
											/>
											{isSameUser && (
												<Button onClick={() => setIsReviewModalVisible(true)} shape="round" type="primary">
													Leave Review
												</Button>
											)}
										</Space>
									</Card>
								</Col>
								<Col xs={0} sm={0} md={0} lg={24} xl={24}>
									<Card style={{ backgroundColor: '#F5F5F5', boxShadow: 'none' }}>
										<Title level={3}>Fluent in</Title>
										<Space wrap>
											{experts?.expert?.profile?.user.languages.map((v) => (
												<Paragraph key={v.iso}>{getLanguageName(v.iso)}</Paragraph>
											))}
											{!experts?.expert?.profile?.user.languages.length && 'English'}
										</Space>
									</Card>
								</Col>
								<Col xs={0} sm={0} md={0} lg={24} xl={24}>
									<Card style={{ backgroundColor: '#F5F5F5', boxShadow: 'none' }}>
										<Title level={3}>Location</Title>
										<Space wrap>{experts?.expert?.profile?.user.country.name}</Space>
									</Card>
								</Col>
							</Row>
						</Col>
						<Col xs={24} lg={18}>
							<Space direction="vertical" style={{ minWidth: '100%' }}>
								<Row align="middle" justify="center" gutter={[16, 16]}>
									<Col xs={{ flex: 'auth' }} sm={{ flex: 'auth' }} md={{ flex: 'auth' }} lg={0} xl={0}>
										<ProfileAvatar
											size={{ xs: 80, sm: 100, md: 120, lg: 0, xl: 0, xxl: 0 }}
											src={experts?.expert?.profile?.user?.profile_pic_url}
										/>
									</Col>
									<Col flex="auto">
										<Title level={2} style={{ fontSize: 24, fontWeight: 'bold', display: 'inline-block' }}>
											{experts?.expert?.profile?.user?.first_name} {experts?.expert?.profile?.user?.last_name}
										</Title>
									</Col>
									<Col>
										{experts?.expert?.profile?.pages?.length > 1 && (
											<>
												<div>
													<Title level={5} style={{ color: '#595962' }}>
														Expert Page:
													</Title>
												</div>
												<Select style={{ width: 250 }} onChange={changeExpertPage} value={selectedExpertPage?.id}>
													{experts.expert.profile.pages.map((page) => (
														<Select.Option key={page.id} value={page.id}>
															{page.channel?.name}
														</Select.Option>
													))}
												</Select>
											</>
										)}
									</Col>
									<Col xs={24} sm={24} md={24} lg={0} xl={0}>
										<Space wrap>
											{experts?.expert?.keywords.map((keyword: TKeyword) => (
												<Button
													key={keyword.id + keyword.keyword}
													type="link"
													onClick={handleSearchKeyword(keyword)}
													style={{ padding: 0, height: 20 }}
												>
													#{keyword.keyword}
												</Button>
											))}
										</Space>
									</Col>
								</Row>
								<Row wrap={false} gutter={[16, 0]}>
									<Col flex="auto">
										<Title level={3} style={{ fontSize: 18, fontWeight: 'bold', display: 'inline-block' }}>
											{experts?.expert?.title}
										</Title>
									</Col>
									{isSameUser && (
										<Col className="hidden-desktop">
											<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
												{currentBrand.showCallPrice && !selectedChannel?.integration_config.hide_call_pricing && (
													<CallPrice payRate={experts?.expert?.profile.pay_rate} />
												)}
												<Button
													disabled={Boolean(isExpertProfileNotFilled)}
													shape="round"
													type="primary"
													onClick={handleRequestCall}
												>
													Request A Call
												</Button>
												{brand.currentBrand?.how_it_works_intro && (
													<a
														style={{
															display: 'block',
															textDecoration: 'underline',
															color: '#595959',
															textAlign: 'center',
														}}
														role="button"
														onClick={handleHowItWorks}
													>
														How it works
													</a>
												)}
											</div>
										</Col>
									)}
								</Row>
								<Divider />
								<Row wrap={false} gutter={[16, 16]}>
									<Col flex="auto">
										<Paragraph style={{ fontSize: 16, maxWidth: '75%' }}>{experts?.expert?.body}</Paragraph>
									</Col>
									{isSameUser && (
										<Col className="hidden-mobile">
											<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
												{currentBrand.showCallPrice && !selectedChannel?.integration_config.hide_call_pricing && (
													<CallPrice payRate={experts?.expert?.profile.pay_rate} />
												)}
												<Button
													disabled={Boolean(isExpertProfileNotFilled)}
													shape="round"
													type="primary"
													onClick={handleRequestCall}
												>
													Request A Call
												</Button>
												{brand.currentBrand?.how_it_works_intro && (
													<a
														style={{
															display: 'block',
															textDecoration: 'underline',
															color: '#595959',
															textAlign: 'center',
														}}
														role="button"
														onClick={handleHowItWorks}
													>
														How it works
													</a>
												)}
											</div>
										</Col>
									)}
								</Row>
								{isSameUser && (
									<Row>
										<Col flex="auto">
											<Space wrap>
												{!!experts?.expert?.calls && experts?.expert?.calls.length === 0 ? (
													<Tooltip
														placement="topLeft"
														title={`Chat is available when you schedule a call or had scheduled a call in the past with ${experts?.expert?.profile?.user?.first_name}`}
													>
														<Button
															shape="round"
															disabled={
																!auth.token ||
																!!chat.loading ||
																(!!experts?.expert?.calls && experts?.expert?.calls.length === 0)
															}
															loading={!!chat.loading}
														>
															{auth.token ? 'Chat' : 'Log In To Chat'}
														</Button>
													</Tooltip>
												) : (
													<Button
														shape="round"
														type="primary"
														onClick={handleChat(experts?.expert?.profile?.user?.id)}
														disabled={!auth.token || !!chat.loading}
														loading={!!chat.loading}
													>
														{auth.token ? 'Chat' : 'Log In To Chat'}
													</Button>
												)}

												{auth.user?.is_admin && (
													<>
														<Button shape="round" type="primary" onClick={() => setAdminReportModal(true)}>
															Admin report
														</Button>
														<AdminReportModal
															isVisible={isAdminReportModal}
															onChange={(status) => setAdminReportModal(status)}
															expert={experts?.expert}
														/>
														<Button shape="round" type="primary" onClick={() => manageAccount()}>
															Manage
														</Button>
													</>
												)}

												{auth.user && <FavoritesButton isFavorite={isFavorite} onClick={handleFavoritesClick} />}
											</Space>
										</Col>
									</Row>
								)}
								<Divider />
								<Row>
									<Col flex="auto">
										<Title level={3}>About</Title>
										<Paragraph style={{ fontSize: 14, lineHeight: '24px', whiteSpace: 'pre-line', maxWidth: '90%' }}>
											{experts?.expert?.bio && <p dangerouslySetInnerHTML={{ __html: experts?.expert?.bio }} />}
										</Paragraph>
									</Col>
								</Row>
								{!!experts?.expert?.profile?.user?.certifications.length && (
									<Row>
										<Col flex="auto">
											<Title level={3}>Certifications</Title>
											{experts?.expert?.profile?.user?.certifications
												.sort(sortCertifications)
												.map((certificate: ICertification) => (
													<div key={certificate.id + 'certificate'}>
														<Paragraph strong style={{ marginBottom: 0 }}>
															{certificate.name}
														</Paragraph>
														<Paragraph>{certificate.issuing_organization}</Paragraph>
													</div>
												))}
										</Col>
									</Row>
								)}
								{!!experts?.expert?.profile?.user?.reviews?.length && (
									<Row>
										<Col flex="auto">
											<Title level={3}>Reviews</Title>
											{experts?.expert?.profile?.user?.reviews?.map((review) => (
												<Review key={review.id + '_review'} review={review} user={experts?.expert?.profile?.user} />
											))}
										</Col>
									</Row>
								)}
								<Row>
									{!!experts?.expert?.profile?.user?.expertInfo?.expert_rating && (
										<Col xs={24} sm={24} md={24} lg={0} xl={0} style={{ marginBottom: 20 }}>
											<Card style={{ boxShadow: 'none' }}>
												<Title level={3}>Rating</Title>
												<Space direction="vertical" size="large">
													<Rate
														className="expert-rate"
														value={experts?.expert?.profile?.user?.expertInfo?.expert_rating}
														disabled
														allowHalf
													/>
													{isSameUser && (
														<Button onClick={() => setIsReviewModalVisible(true)} shape="round" type="primary">
															Leave Review
														</Button>
													)}
												</Space>
											</Card>
										</Col>
									)}
									<Col xs={24} sm={24} md={24} lg={0} xl={0}>
										<Card style={{ backgroundColor: '#F5F5F5', boxShadow: 'none' }}>
											<Title level={3}>Fluent in</Title>
											<Space wrap>
												{experts?.expert?.profile?.user.languages.map((v) => (
													<Paragraph key={v.iso}>{getLanguageName(v.iso)}</Paragraph>
												))}
												{!experts?.expert?.profile?.user.languages.length && 'English'}
											</Space>
										</Card>
									</Col>
									<Col xs={24} sm={24} md={24} lg={0} xl={0}>
										<Card style={{ backgroundColor: '#F5F5F5', boxShadow: 'none' }}>
											<Title level={3}>Location</Title>
											<Space wrap>{experts?.expert?.profile?.user.country.name}</Space>
										</Card>
									</Col>
								</Row>
							</Space>
						</Col>
					</Row>
				</Skeleton>
			</Card>
			<BookingModal visible={isModalVisible} onCancel={handleCancel} expert={experts.expert} />
			<ReviewModal
				visible={isReviewModalVisible}
				onAction={() => actions.getExpertBySlug(slug)}
				onCancel={() => setIsReviewModalVisible(false)}
				expertUser={experts?.expert?.profile.user}
			/>
		</>
	)
}

const ExpertPage = (props: any) => {
	return <ExpertSlugPage {...props} />
}

export default ExpertPage
